import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import type { ProductDetail as ProductDetailType, ProductComment, Language, ReviewStatus } from '../types/product';
import { LANGUAGES } from '../types/product';
import SeoScoreCell from './SeoScoreCell';
import SellingBadge from './SellingBadge';

interface Props {
  cprcode: string;
  onClose: () => void;
  onSaved: () => void;
}

type PanelView = 'edit' | 'original' | 'comments';

const REVIEW_STYLES: Record<string, { bg: string; color: string; border: string; label: string }> = {
  approved: { bg: '#ecfdf5', color: '#065f46', border: '#6ee7b7', label: 'Approved' },
  changes_requested: { bg: '#fffbeb', color: '#92400e', border: '#fbbf24', label: 'Changes Requested' },
  denied: { bg: '#fef2f2', color: '#991b1b', border: '#fca5a5', label: 'Denied' },
  pending: { bg: '#f0f9ff', color: '#1e40af', border: '#93c5fd', label: 'Pending Review' },
};

function ReviewBadge({ status }: { status: ReviewStatus }) {
  if (!status) return null;
  const s = REVIEW_STYLES[status] || REVIEW_STYLES.pending;
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {s.label}
    </span>
  );
}

const fieldsByLang: Record<Language, { key: string; label: string }[]> = {
  en: [
    { key: 'short_description_en', label: 'Short Description' },
    { key: 'long_description_en', label: 'Long Description' },
    { key: 'content_en', label: 'Content' },
    { key: 'meta_title', label: 'Meta Title' },
    { key: 'meta_description', label: 'Meta Description' },
    { key: 'meta_keywords', label: 'Meta Keywords' },
  ],
  th: [
    { key: 'short_description_th', label: 'Short Description' },
    { key: 'long_description_th', label: 'Long Description' },
    { key: 'content_th', label: 'Content' },
    { key: 'meta_title_th', label: 'Meta Title' },
    { key: 'meta_description_th', label: 'Meta Description' },
  ],
  fr: [
    { key: 'short_description_fr', label: 'Short Description' },
    { key: 'long_description_fr', label: 'Long Description' },
    { key: 'meta_title_fr', label: 'Meta Title' },
    { key: 'meta_description_fr', label: 'Meta Description' },
  ],
  de: [
    { key: 'short_description_de', label: 'Short Description' },
    { key: 'long_description_de', label: 'Long Description' },
    { key: 'meta_title_de', label: 'Meta Title' },
    { key: 'meta_description_de', label: 'Meta Description' },
  ],
  zh: [
    { key: 'short_description_zh', label: 'Short Description' },
    { key: 'long_description_zh', label: 'Long Description' },
    { key: 'meta_title_zh', label: 'Meta Title' },
    { key: 'meta_description_zh', label: 'Meta Description' },
  ],
  ru: [
    { key: 'short_description_ru', label: 'Short Description' },
    { key: 'long_description_ru', label: 'Long Description' },
    { key: 'meta_title_ru', label: 'Meta Title' },
    { key: 'meta_description_ru', label: 'Meta Description' },
  ],
};

function formatTimestamp(ts: number): string {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export default function ProductDetail({ cprcode, onClose, onSaved }: Props) {
  const [product, setProduct] = useState<ProductDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<Language>('en');
  const [panelView, setPanelView] = useState<PanelView>('edit');
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Comments state
  const [comments, setComments] = useState<ProductComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);

  // Review state
  const [reviewSaving, setReviewSaving] = useState(false);
  const [showReviewNote, setShowReviewNote] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [pendingReviewAction, setPendingReviewAction] = useState<ReviewStatus | null>(null);

  useEffect(() => {
    setLoading(true);
    api.getProduct(cprcode).then((p) => {
      setProduct(p);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [cprcode]);

  useEffect(() => {
    if (panelView === 'comments') {
      setCommentsLoading(true);
      api.getComments(cprcode).then((r) => {
        setComments(r.comments);
        setCommentsLoading(false);
      }).catch(() => setCommentsLoading(false));
    }
  }, [cprcode, panelView]);

  const handleClose = useCallback(() => {
    if (Object.keys(edits).length > 0) {
      if (!window.confirm('You have unsaved changes. Discard?')) return;
    }
    onClose();
  }, [edits, onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  const handleFieldChange = (key: string, value: string) => {
    setEdits((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
    setError(null);
  };

  const getFieldValue = (key: string): string => {
    if (key in edits) return edits[key];
    return (product as unknown as Record<string, string>)?.[key] || '';
  };

  const handleSave = async () => {
    if (Object.keys(edits).length === 0) return;
    setSaving(true);
    try {
      await api.updateProduct(cprcode, edits as Partial<ProductDetailType>);
      setEdits({});
      setSaved(true);
      const updated = await api.getProduct(cprcode);
      setProduct(updated);
      onSaved();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(`Save failed: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setCommentSaving(true);
    try {
      await api.addComment(cprcode, commentText.trim());
      setCommentText('');
      const r = await api.getComments(cprcode);
      setComments(r.comments);
    } catch {
      setError('Failed to add comment');
    } finally {
      setCommentSaving(false);
    }
  };

  const handleReviewAction = (action: ReviewStatus) => {
    if (action === 'changes_requested' || action === 'denied') {
      setPendingReviewAction(action);
      setShowReviewNote(true);
      return;
    }
    submitReview(action, '');
  };

  const submitReview = async (action: ReviewStatus, note: string) => {
    setReviewSaving(true);
    try {
      await api.reviewProduct(cprcode, action, note || undefined);
      const updated = await api.getProduct(cprcode);
      setProduct(updated);
      setShowReviewNote(false);
      setReviewNote('');
      setPendingReviewAction(null);
      onSaved();
    } catch {
      setError('Failed to submit review');
    } finally {
      setReviewSaving(false);
    }
  };

  const hasEdits = Object.keys(edits).length > 0;

  const viewBtnStyle = (active: boolean) => ({
    padding: '6px 14px',
    border: 'none',
    borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
    background: 'none',
    color: active ? '#2563eb' : '#6b7280',
    fontWeight: active ? 600 : 400 as number,
    fontSize: 13,
    cursor: 'pointer' as const,
  });

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 700, background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
      <div onClick={handleClose} style={{ position: 'fixed', top: 0, left: 0, right: 700, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 999 }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{product?.display_name || cprcode}</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>CPR: {cprcode}</div>
        </div>
        <button onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280', padding: 4 }}>
          &times;
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading...</div>
      ) : product ? (
        <>
          {/* Product info bar */}
          <div style={{ display: 'flex', gap: 16, padding: '12px 20px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
            <div><span style={{ fontSize: 12, color: '#6b7280' }}>Brand:</span> <span style={{ fontSize: 13, fontWeight: 500 }}>{product.brand}</span></div>
            <div><span style={{ fontSize: 12, color: '#6b7280' }}>Category:</span> <span style={{ fontSize: 13, fontWeight: 500 }}>{product.category}</span></div>
            <div><span style={{ fontSize: 12, color: '#6b7280' }}>SEO:</span> <SeoScoreCell value={product.seo_score} /></div>
            <div><SellingBadge value={product.is_selling} /></div>
            {product.review_status && <ReviewBadge status={product.review_status} />}
          </div>

          {/* Review actions bar */}
          <div style={{ display: 'flex', gap: 8, padding: '10px 20px', borderBottom: '1px solid #e5e7eb', background: '#fafafa', flexShrink: 0, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#6b7280', marginRight: 4 }}>Review:</span>
            <button
              onClick={() => handleReviewAction('approved')}
              disabled={reviewSaving}
              style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, border: '1px solid #6ee7b7', borderRadius: 6, cursor: 'pointer', background: product.review_status === 'approved' ? '#ecfdf5' : 'white', color: '#065f46', opacity: reviewSaving ? 0.5 : 1 }}
            >
              Approve
            </button>
            <button
              onClick={() => handleReviewAction('changes_requested')}
              disabled={reviewSaving}
              style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, border: '1px solid #fbbf24', borderRadius: 6, cursor: 'pointer', background: product.review_status === 'changes_requested' ? '#fffbeb' : 'white', color: '#92400e', opacity: reviewSaving ? 0.5 : 1 }}
            >
              Request Changes
            </button>
            <button
              onClick={() => handleReviewAction('denied')}
              disabled={reviewSaving}
              style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', background: product.review_status === 'denied' ? '#fef2f2' : 'white', color: '#991b1b', opacity: reviewSaving ? 0.5 : 1 }}
            >
              Deny
            </button>
            {product.review_by && (
              <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 8 }}>
                Last reviewed by {product.review_by} {product.review_at ? `on ${formatTimestamp(product.review_at)}` : ''}
              </span>
            )}
          </div>

          {/* Review note modal */}
          {showReviewNote && (
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #e5e7eb', background: pendingReviewAction === 'denied' ? '#fef2f2' : '#fffbeb', flexShrink: 0 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                {pendingReviewAction === 'denied' ? 'Reason for denial:' : 'What changes are needed?'}
              </label>
              <textarea
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder={pendingReviewAction === 'denied' ? 'Explain why this content is denied...' : 'Describe the changes needed...'}
                rows={3}
                autoFocus
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowReviewNote(false); setPendingReviewAction(null); setReviewNote(''); }}
                  style={{ padding: '6px 14px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', background: 'white' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => pendingReviewAction && submitReview(pendingReviewAction, reviewNote)}
                  disabled={!reviewNote.trim() || reviewSaving}
                  style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 4, cursor: 'pointer', background: pendingReviewAction === 'denied' ? '#ef4444' : '#f59e0b', color: 'white', opacity: (!reviewNote.trim() || reviewSaving) ? 0.5 : 1 }}
                >
                  {reviewSaving ? 'Submitting...' : 'Submit'}
                </button>
              </div>
              {product.review_note && product.review_status && (
                <div style={{ marginTop: 8, padding: 8, background: 'white', borderRadius: 4, border: '1px solid #e5e7eb', fontSize: 12, color: '#6b7280' }}>
                  <strong>Previous note:</strong> {product.review_note}
                </div>
              )}
            </div>
          )}

          {/* View selector: Edit | Original | Comments */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', flexShrink: 0, padding: '0 20px', gap: 4 }}>
            <button onClick={() => setPanelView('edit')} style={viewBtnStyle(panelView === 'edit')}>
              Edit Content
            </button>
            <button onClick={() => setPanelView('original')} style={viewBtnStyle(panelView === 'original')}>
              Original vs Processed
            </button>
            <button onClick={() => setPanelView('comments')} style={viewBtnStyle(panelView === 'comments')}>
              Comments {comments.length > 0 && `(${comments.length})`}
            </button>
          </div>

          {/* ---- EDIT VIEW ---- */}
          {panelView === 'edit' && (
            <>
              {/* Language tabs */}
              <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', flexShrink: 0, padding: '0 20px' }}>
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setActiveTab(lang.code)}
                    style={{
                      padding: '10px 16px',
                      border: 'none',
                      borderBottom: activeTab === lang.code ? '2px solid #2563eb' : '2px solid transparent',
                      background: 'none',
                      color: activeTab === lang.code ? '#2563eb' : '#6b7280',
                      fontWeight: activeTab === lang.code ? 600 : 400,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    {lang.flag} {lang.name}
                  </button>
                ))}
              </div>

              <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
                {fieldsByLang[activeTab].map((f) => {
                  const val = getFieldValue(f.key);
                  const isLong = f.key.includes('long_description') || f.key.includes('content');
                  const isEdited = f.key in edits;

                  return (
                    <div key={f.key} style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                        {f.label}
                        {(f.key === 'meta_title' || f.key === 'meta_description') && val && <span style={{ fontWeight: 400, color: '#9ca3af' }}> ({val.length} chars)</span>}
                        {isEdited && <span style={{ color: '#f59e0b', marginLeft: 6 }}>modified</span>}
                      </label>
                      {isLong ? (
                        <textarea
                          value={val}
                          onChange={(e) => handleFieldChange(f.key, e.target.value)}
                          rows={8}
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', borderColor: isEdited ? '#f59e0b' : '#d1d5db' }}
                        />
                      ) : (
                        <input
                          value={val}
                          onChange={(e) => handleFieldChange(f.key, e.target.value)}
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, boxSizing: 'border-box', borderColor: isEdited ? '#f59e0b' : '#d1d5db' }}
                        />
                      )}
                    </div>
                  );
                })}

                {activeTab === 'en' && (
                  <div style={{ marginTop: 8 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                      Schema.org JSON-LD
                    </label>
                    <textarea
                      value={getFieldValue('schema_jsonld')}
                      onChange={(e) => handleFieldChange('schema_jsonld', e.target.value)}
                      rows={6}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* ---- ORIGINAL VS PROCESSED VIEW ---- */}
          {panelView === 'original' && (
            <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12, borderBottom: '1px solid #e5e7eb', paddingBottom: 8 }}>
                  English
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Original (Ground Truth)
                    </label>
                    <div style={{ padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 4, fontSize: 13, minHeight: 60, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {product.original_content_en || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No original content</span>}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Processed (Generated)
                    </label>
                    <div style={{ padding: 10, background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 4, fontSize: 13, minHeight: 60, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {product.short_description_en || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No processed content</span>}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12, borderBottom: '1px solid #e5e7eb', paddingBottom: 8 }}>
                  Thai
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Original (Ground Truth)
                    </label>
                    <div style={{ padding: 10, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 4, fontSize: 13, minHeight: 60, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {product.original_content_th || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No original content</span>}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Processed (Generated)
                    </label>
                    <div style={{ padding: 10, background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 4, fontSize: 13, minHeight: 60, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {product.short_description_th || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No processed content</span>}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12, borderBottom: '1px solid #e5e7eb', paddingBottom: 8 }}>
                  Full Generated Content (EN)
                </h3>
                <div style={{ padding: 10, background: '#f0f9ff', border: '1px solid #93c5fd', borderRadius: 4, fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {product.long_description_en || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No long description</span>}
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 12, borderBottom: '1px solid #e5e7eb', paddingBottom: 8 }}>
                  SEO Metadata
                </h3>
                <div style={{ display: 'grid', gap: 8 }}>
                  {[
                    { label: 'Meta Title (EN)', value: product.meta_title },
                    { label: 'Meta Description (EN)', value: product.meta_description },
                    { label: 'Meta Keywords', value: (product as unknown as Record<string, string>).meta_keywords },
                  ].map((m) => (
                    <div key={m.label}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>{m.label}</label>
                      <div style={{ padding: 8, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 13 }}>
                        {m.value || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Empty</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ---- COMMENTS VIEW ---- */}
          {panelView === 'comments' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
                {commentsLoading ? (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: 20 }}>Loading comments...</div>
                ) : comments.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>
                    No comments yet. Be the first to leave feedback.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {comments.map((c) => (
                      <div key={c.comment_id} style={{ padding: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{c.user}</span>
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>{formatTimestamp(c.timestamp)}</span>
                        </div>
                        <div style={{ fontSize: 13, color: '#1f2937', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{c.text}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Comment input */}
              <div style={{ borderTop: '1px solid #e5e7eb', padding: '12px 20px', flexShrink: 0 }}>
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleAddComment}
                    disabled={!commentText.trim() || commentSaving}
                    style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 4, fontSize: 13, cursor: 'pointer', opacity: (!commentText.trim() || commentSaving) ? 0.5 : 1 }}
                  >
                    {commentSaving ? 'Posting...' : 'Post Comment'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Footer (only for edit view) */}
          {panelView === 'edit' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                {error && <span style={{ color: '#ef4444' }}>{error}</span>}
                {!error && saved && <span style={{ color: '#15803d' }}>Saved successfully</span>}
                {!error && hasEdits && !saved && <span style={{ color: '#f59e0b' }}>{Object.keys(edits).length} unsaved change(s)</span>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleClose}
                  style={{ padding: '8px 16px', background: 'white', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={handleSave} disabled={!hasEdits || saving}
                  style={{ padding: '8px 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 4, fontSize: 13, cursor: 'pointer', opacity: (!hasEdits || saving) ? 0.5 : 1 }}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>Failed to load product</div>
      )}
    </div>
  );
}
